// WN2 (Wave 4): web "Remake TeeUp" field-copy contract, matching the mobile reference contract
// 1:1 (app/utils/remake/{getNewTeeupDataOnRemake,updateTeeupDataToRemake,extractInviteesFromExistingTeeup}.ts
// in teeup-mobile). Mobile has NO backend clone/duplicate route -- "Remake" is a pure client-side
// pre-fill into the standard Create-TeeUp flow, which then submits through the same POST
// /teeup/create endpoint every normal create uses. This util reproduces that same pre-fill on web.
//
// Contract (confirmed from mobile source, not invented):
// - type, name, photo copied as-is from the source TeeUp.
// - invited: every current participant EXCEPT the current user (no RSVP/status carried over --
//   they are re-invited fresh, exactly like a brand-new TeeUp).
// - gameplans: ONLY suggestions that were DECIDED are carried over (undecided/rejected
//   suggestions are dropped entirely, matching mobile).
//   - When: a decided When suggestion is only kept if it is not cancelled AND its end date is
//     within [-2, +7] days of "now" (mobile's diff window) -- in that case both start/end are
//     shifted forward exactly 7 days. Outside that window (including any cancelled TeeUp), the
//     When suggestion is dropped entirely (so a stale/expired or cancelled remake never opens
//     with a Where the past).
//   - Where: decided Where suggestions are carried over unchanged, no date logic (Where has no
//     date field).
// - Messages/chat history: never copied (mobile's params has no message field).
// - Status: always resets to a fresh Planning-stage create (this is a brand-new TeeUp submit,
//   there is no "status" field in the create payload at all).
import moment from "moment-timezone"

import { teeupStatusKeys } from "@config/enums"

const GAMEPLAN_TITLE_WHEN = "When"
const GAMEPLAN_TITLE_WHERE = "Where"

const isSuggestionDecided = suggestion =>
  Boolean(suggestion?.isDecided || suggestion?.decided)

// The suggestion objects on `teeup.gameplans[].suggestions` are not 100% uniformly shaped
// across every read path in this codebase -- some carry startDate/endDate/latitude/longitude
// flat (the `formatSuggestion` normalization in gamePlanUtils.js), others nest a `when`/`where`
// sub-object (seen in teeupList/TeeupItem's own display code). Normalize to the FLAT shape here,
// since that is what the create-flow's submit path (createTeeup -> formatSuggestionOut) expects
// -- the create-flow reducer (teeupCreateReducer.js) just spreads whatever object it is given.
const normalizeSuggestion = suggestion => {
  const nested = suggestion?.when || suggestion?.where || {}
  return {
    ...suggestion,
    startDate: suggestion?.startDate || nested.startDate,
    endDate: suggestion?.endDate || nested.endDate,
    value: suggestion?.value ?? nested.value,
    latitude: suggestion?.latitude ?? nested.latitude,
    longitude: suggestion?.longitude ?? nested.longitude,
  }
}

// Mirrors mobile's updateTeeupDataToRemake.ts diff window exactly: only shift+keep a decided
// When suggestion if `now` is between 2 days before its end and 7 days after its end.
const buildRemakeWhenSuggestions = (isCancelled, whenGameplan) => {
  if (!whenGameplan) return []
  if (isCancelled) return []

  const decided = (whenGameplan.suggestions || [])
    .filter(isSuggestionDecided)
    .map(normalizeSuggestion)

  return decided
    .map(suggestion => {
      if (!suggestion.startDate || !suggestion.endDate) return null

      const startDate = moment(suggestion.startDate)
      const endDate = moment(suggestion.endDate)
      const now = moment()
      const diffDays = now.diff(endDate, "days")

      if (diffDays > -2 && diffDays <= 7) {
        return {
          ...suggestion,
          startDate: startDate.add(7, "days").format(),
          endDate: endDate.add(7, "days").format(),
        }
      }
      return null
    })
    .filter(Boolean)
}

const buildRemakeWhereSuggestions = whereGameplan => {
  if (!whereGameplan) return []
  return (whereGameplan.suggestions || [])
    .filter(isSuggestionDecided)
    .map(normalizeSuggestion)
}

// Returns { whenSuggestions, whereSuggestions } ready to dispatch via
// updateGamePlanWhenSuggestions / updateGamePlanWhereSuggestions (teeupCreateReducer.js).
// `teeup` needs only `.status` and `.gameplans` (array of {title, suggestions}) -- see
// gameplanOptionsToGameplans below for the TeeUpsNd/inboxStore adapter, which carries
// `gameplanOptions` as {type, suggestions} instead (no title).
export const getRemakeGameplanSuggestions = teeup => {
  const gameplans = teeup?.gameplans || []
  const whenGameplan = gameplans.find(
    g => g.title === GAMEPLAN_TITLE_WHEN || g.type === "when"
  )
  const whereGameplan = gameplans.find(
    g => g.title === GAMEPLAN_TITLE_WHERE || g.type === "where"
  )
  const isCancelled = teeup?.status === teeupStatusKeys.cancelled

  return {
    whenSuggestions: buildRemakeWhenSuggestions(isCancelled, whenGameplan),
    whereSuggestions: buildRemakeWhereSuggestions(whereGameplan),
  }
}

// invitees passed in is the teeup's people map ({[userId]: TeeupInvitee}), same shape mobile's
// extractInviteesFromExistingTeeup consumes -- excludes the current user, carries every other
// participant over as a fresh invite.
export const extractRemakeInvitees = (people = {}, currentUserId) =>
  Object.values(people).filter(
    person => Number(person?.id) !== Number(currentUserId)
  )

// Top-level draft consumed by CreateTeeUpProvider's lazy initial state (name/image/invitees --
// the local React Context fields, NOT the Redux gameplan slice, which is seeded separately via
// getRemakeGameplanSuggestions above at the same call site).
export const buildRemakeDraft = (teeup, people, currentUserId) => ({
  name: teeup?.name || "",
  image: teeup?.photo || null,
  invitees: extractRemakeInvitees(people, currentUserId),
})

// Adapter for the live TeeUpsNd/TeeUpList/Card kebab menu (the ACTUAL default Inbox card
// renderer -- TeeUpsPage.js only swaps in the legacy teeupList/TeeupItem tree for the
// "Coming Up" view). That surface holds participants as a flat array (useInboxStore's
// `teeupParticipants[teeUp.id]`, shape = types/participant.ts Participant[]) rather than the
// {[userId]: TeeupInvitee} map the legacy tree uses, and gameplan suggestions as
// `gameplanOptions` ({type, suggestions}[]) rather than `teeup.gameplans` ({title,
// suggestions}[]) -- both adapters below normalize to what getRemakeGameplanSuggestions /
// buildRemakeDraft already expect, so the SAME field-copy contract runs on both surfaces.
const COOE_USER_TYPE = "cooeUser"
const COOE_USER_TYPE_ID = 1 // userTypeIds.normal, config/enums.js

// A Participant (types/participant.ts) has no `type`/`userType` marker the create-flow's
// isCooeUser()/formatInvitees() checks for (it is a teeup-membership record, not a contact
// record) -- tag it as a normal CooWe user explicitly so it re-invites correctly instead of
// silently falling through to the manual-contact branch and getting dropped.
export const buildRemakeInviteeFromParticipant = participant => ({
  id: participant.userId ?? participant.id,
  cooeId: participant.userId ?? participant.id,
  type: COOE_USER_TYPE,
  userType: COOE_USER_TYPE_ID,
  usertype: COOE_USER_TYPE_ID,
  name: participant.name || participant.username,
  username: participant.username,
  avatar: participant.avatar,
})

export const extractRemakeInviteesFromParticipants = (
  participants = [],
  currentUserId
) =>
  (participants || [])
    .filter(p => Number(p?.userId ?? p?.id) !== Number(currentUserId))
    .map(buildRemakeInviteeFromParticipant)

// gameplanOptions: [{type: "when"|"where", suggestions: [...]}], teeUp: {status, name, photo}
export const buildRemakeDraftFromCard = (teeUp, participants, currentUserId) => ({
  name: teeUp?.name || "",
  image: teeUp?.photo || null,
  invitees: extractRemakeInviteesFromParticipants(participants, currentUserId),
})

// teeUp.status here is the NUMERIC TEEUP_STATUS enum (types/teeup.ts), not the string
// teeupStatusKeys the legacy surface uses -- isCancelled is passed in already resolved by the
// caller (Card/index.tsx already computes TEEUP_STATUS.CANCELLED === teeUp.status for isGrayOut).
export const getRemakeGameplanSuggestionsFromOptions = (
  gameplanOptions,
  isCancelled
) => {
  const gameplans = (gameplanOptions || []).map(option => ({
    type: option.type,
    title: option.type === "when" ? GAMEPLAN_TITLE_WHEN : GAMEPLAN_TITLE_WHERE,
    suggestions: option.suggestions || [],
  }))
  const whenGameplan = gameplans.find(g => g.type === "when")
  const whereGameplan = gameplans.find(g => g.type === "where")

  return {
    whenSuggestions: buildRemakeWhenSuggestions(isCancelled, whenGameplan),
    whereSuggestions: buildRemakeWhereSuggestions(whereGameplan),
  }
}
